// localStorage-backed expand/collapse state for the email folder tree
// in the sidebar.
//
// Without this, each folder node tracked its expanded/collapsed state
// only in local React component state, seeded from a hardcoded
// default (top-level system folders with children start expanded;
// everything else starts collapsed). That default re-applied on every
// full page load, silently re-opening any subfolder the advisor had
// manually collapsed — the choice never survived a refresh.
//
// Stores only the folders the user has EXPLICITLY toggled (not every
// folder's current state), so a folder added to the mailbox after the
// user's last visit still falls back to the sensible depth/well-known
// default rather than an absent entry meaning "collapsed."
//
// Keyed the same way favorites and folder-prefs are: Microsoft
// well-known name preferred (stable across reconnects), folder UUID
// fallback for custom folders. Per-device by design (see CLAUDE.md's
// localStorage scope rule) — same tier as folder favorites/ordering,
// not synced across an advisor's devices.

const STORAGE_KEY = "stewardship-os.email.folder-expand.v1";

// Resolve localStorage off `globalThis` so this module typechecks
// against both the root tsconfig (no DOM lib — tests pull this file
// in via tests/web/) and the web tsconfig, matching email-folder-prefs.ts.
interface BrowserStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
function getStorage(): BrowserStorage | null {
  const g = globalThis as unknown as { localStorage?: BrowserStorage };
  return g.localStorage ?? null;
}

function safeRead(): Record<string, boolean> {
  const storage = getStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const out: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(
        parsed as Record<string, unknown>,
      )) {
        if (typeof value === "boolean") out[key] = value;
      }
      return out;
    }
    return {};
  } catch {
    return {};
  }
}

function safeWrite(state: Record<string, boolean>): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / SecurityError — non-fatal. Expand state just won't persist.
  }
}

export function getFolderExpandState(): Record<string, boolean> {
  return safeRead();
}

export function setFolderExpanded(
  id: string,
  expanded: boolean,
): Record<string, boolean> {
  const state = safeRead();
  state[id] = expanded;
  safeWrite(state);
  return state;
}
